export const isProblemDescription = (txt) => {
  const normalized = (txt || "").toLowerCase();
  const hints = [
    "panne",
    "ne ",
    "marche pas",
    "ne fonctionne pas",
    "s'allume pas",
    "n’allume pas",
    "erreur",
    "code",
    "fuite",
    "chauffe pas",
    "refroidit pas",
    "bruit",
    "odeur",
    "bloqué",
    "bloquee",
    "bloquée",
    "clignote",
    "vibre",
    "tremble",
    "l'eau",
    "eau",
  ];
  return hints.some((hint) => normalized.includes(hint));
};

const wait = (ms = 750) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const escapeHtml = (value = "") =>
  `${value}`
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const guideLibrary = [
  {
    match: /(lave[- ]?linge|machine a laver|washing machine)/i,
    guides: [
      {
        title: "Vérifier la pompe de vidange",
        url: "https://www.ifixit.com/Guide/How+to+Diagnose+Washer+Drain+Issues/37607",
        summary:
          "Inspecte la pompe et retire les objets qui peuvent bloquer l'évacuation.",
      },
      {
        title: "Diagnostiquer un tambour bloqué",
        url: "https://www.ifixit.com/Guide/Diagnosing+a+Drum+that+Won%E2%80%99t+Turn/37850",
        summary:
          "Guide pas-à-pas pour identifier les causes d'un tambour immobile.",
      },
    ],
  },
  {
    match: /(frigo|réfrigérateur|refrigerateur)/i,
    guides: [
      {
        title: "Réparer un frigo qui ne refroidit plus",
        url: "https://www.ifixit.com/Guide/Refrigerator+Not+Cooling/20048",
        summary: "Vérifie le thermostat, le compresseur et les ventilateurs.",
      },
      {
        title: "Nettoyer le condenseur",
        url: "https://www.ifixit.com/Guide/Cleaning+Refrigerator+Condenser+Coils/10923",
        summary:
          "Des serpentins obstrués peuvent suffire à déclencher une panne de froid.",
      },
    ],
  },
  {
    match: /(lave[- ]?vaisselle|dishwasher)/i,
    guides: [
      {
        title: "Lave-vaisselle qui n'évacue plus",
        url: "https://www.ifixit.com/Guide/Dishwasher+Not+Draining/120350",
        summary:
          "Procédure courte pour contrôler filtre, durite et turbine de vidange.",
      },
      {
        title: "Résoudre les fuites sous cuve",
        url: "https://www.ifixit.com/Guide/Diagnose+Dishwasher+Leaks/120366",
        summary:
          "Inspecte les joints, le collecteur et les clips de portes rapidement.",
      },
    ],
  },
];

const buildFallbackGuides = (productName = "ton appareil", query = "") => {
  if (!query.trim()) return [];
  return [
    {
      title: `Méthode rapide pour "${query}"`,
      url: "https://www.ifixit.com/Guides",
      summary: `Checklist générique pour identifier l'origine de la panne sur ${productName}.`,
    },
  ];
};

const getLastUserInput = (history = []) =>
  history
    .slice()
    .reverse()
    .find((item) => item.role === "user")?.text || "";

const resolveTransitionTarget = ({
  node,
  status,
  context,
  config,
  history,
  guards,
}) => {
  const branches = node?.branches || node?.transitions || [];
  for (const branch of branches) {
    if (branch.when && status && branch.when === status) {
      return branch.next;
    }
    if (branch.guard && guards?.[branch.guard]) {
      try {
        const guardResult = guards[branch.guard]({
          context,
          node,
          config,
          history,
        });
        if (guardResult) return branch.next;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Guard "${branch.guard}" failed`, error);
      }
    }
    if (branch.else) {
      return branch.next;
    }
  }
  return null;
};

const followNodeTransition = (params) => {
  const next = resolveTransitionTarget(params);
  if (next) {
    params.transitionTo(next);
    return true;
  }
  return false;
};

const renderGuidesHtml = (guides = []) => {
  if (!guides.length) return "";
  return `
    <div class="options-container" style="flex-direction:column;align-items:flex-start;">
      ${guides
        .map(
          (guide, index) => `
            <div style="margin-bottom:12px;">
              <strong>Tutoriel ${index + 1} :</strong>
              <div>
                <a href="${escapeHtml(guide.url)}" target="_blank" rel="noreferrer">
                  ${escapeHtml(guide.title)}
                </a>
              </div>
              ${
                guide.summary
                  ? `<div style="font-size:0.9em;color:#3f3f3f;margin-top:4px;">
                      ${escapeHtml(guide.summary)}
                    </div>`
                  : ""
              }
            </div>
          `
        )
        .join("")}
    </div>
  `;
};

const findGuides = async ({ productName = "", query = "" }) => {
  const normalized = `${productName} ${query}`.trim();
  const match =
    normalized &&
    guideLibrary.find((entry) => entry.match.test(normalized.toLowerCase()));

  await wait(900);

  if (match) return match.guides;
  return buildFallbackGuides(productName || "ton appareil", query);
};

export const repairChatbotActions = {
  
  onUserDescription: ({
    getHistory,
    transitionTo,
    setWaiting,
    node,
    context,
    config,
    guards,
  }) => {
    setWaiting(false);
    const history = getHistory();
    const latestInput = getLastUserInput(history);
    const trimmed = latestInput?.trim();
    let status;
    if (trimmed && isProblemDescription(trimmed)) {
      status = "problem-description";
    }
    followNodeTransition({
      node,
      status,
      transitionTo,
      context,
      config,
      history,
      guards,
    });
  },
  onSearchDIY: async ({
    context,
    getHistory,
    setContext,
    setWaiting,
    transitionTo,
    node,
    config,
    guards,
  }) => {
    setWaiting(true);
    const productName =
      context?.product?.prodName ||
      context?.product?.name ||
      "ton appareil";
    const history = getHistory();
    const lastUserInput = getLastUserInput(history);
    const resolvedQuery =
      lastUserInput || context?.event?.description || "la panne indiquée";
    let guides = [];
    let searchFailed = false;
    try {
      guides = await findGuides({
        productName,
        query: resolvedQuery,
      });
    } catch (error) {
      searchFailed = true;
      // eslint-disable-next-line no-console
      console.error("DIY search failed", error);
    }

    setContext({
      lastDiyResults: guides,
      lastDiyQuery: resolvedQuery,
      diyResultsHtml:
        !searchFailed && guides.length ? renderGuidesHtml(guides) : "",
    });

    setWaiting(false);
    const status = searchFailed
      ? "search-error"
      : guides.length
      ? "results-found"
      : "no-results";
    followNodeTransition({
      node,
      status,
      transitionTo,
      context,
      config,
      history,
      guards,
    });
  },
};

export const repairChatbotLogic = {
  hasBonusFromContext: ({ context }) => Boolean(context?.hasBonus),
};
